import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Shield, TrendingUp, Users, DollarSign, BarChart2, Zap, AlertCircle } from 'lucide-react';

const API = 'https://api.naijapredict.net';

export default function LoginPage() {
  const { setCreator } = useAuth();
  const navigate = useNavigate();
  const tgRef = useRef(null);

  useEffect(() => {
    window.onTelegramAuth = async (user) => {
      try {
        const btn = document.querySelector('.tg-login-status');
        if (btn) btn.textContent = 'Connecting...';
        const res = await fetch(API + '/api/creator/auth', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(user),
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || 'Auth failed');
        }
        const data = await res.json();
        if (data.token) {
          localStorage.setItem('creator_token', data.token);
          setCreator(data.creator);
          navigate('/dashboard');
        }
      } catch(e) {
        console.error('Login error:', e);
        const status = document.querySelector('.tg-login-status');
        if (status) { status.textContent = 'Login failed: ' + e.message; status.style.color = '#ff4444'; }
        else alert('Login failed: ' + e.message);
      }
    };

    // Inject Telegram login widget
    if (tgRef.current && !tgRef.current.querySelector('script')) {
      const script = document.createElement('script');
      script.src = 'https://telegram.org/js/telegram-widget.js?22';
      script.setAttribute('data-telegram-login', 'NaijaPredict_Bot');
      script.setAttribute('data-size', 'large');
      script.setAttribute('data-onauth', 'onTelegramAuth(user)');
      script.setAttribute('data-request-access', 'write');
      script.async = true;
      tgRef.current.appendChild(script);
    }
  }, []);

  const FEATURES = [
    { icon: DollarSign, title: 'Earn 0.5% Royalties',   desc: 'Every trade on your market earns you 0.5% automatically' },
    { icon: TrendingUp, title: 'Zero Capital Required',  desc: 'Platform seeds your markets — you risk nothing' },
    { icon: BarChart2,  title: 'Real-time Analytics',    desc: 'Track volume, traders, and earnings live' },
    { icon: Users,      title: 'Audience Monetization',  desc: 'Turn your Telegram/X audience into passive income' },
    { icon: Zap,        title: 'Instant Payouts',        desc: 'Withdraw royalties to bank or USDC wallet anytime' },
    { icon: Shield,     title: 'Non-Custodial',          desc: 'Smart contracts guarantee fair and transparent payouts' },
  ];

  return (
    <div style={{ minHeight:'100vh', background:'#07090a', display:'flex', fontFamily:"'Inter',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Syne:wght@700;800;900&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
      `}</style>

      {/* Left Panel */}
      <div style={{ flex:1, padding:'48px', display:'flex', flexDirection:'column', justifyContent:'center', maxWidth:600 }}>
        <div style={{ marginBottom:48, display:'flex', alignItems:'center', gap:14 }}>
          <img src="/logo.svg" alt="NaijaPredict" style={{ width:48, height:48, borderRadius:12 }}/>
          <div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:900, fontSize:28, color:'#fff', lineHeight:1 }}>NAIJAPREDICT</div>
            <div style={{ fontSize:13, color:'#00c566', fontWeight:600, letterSpacing:2 }}>CREATOR PORTAL</div>
          </div>
        </div>

        <h1 style={{ fontSize:42, fontWeight:800, lineHeight:1.2, marginBottom:16, fontFamily:"'Syne',sans-serif" }}>
          Monetize Your<br/>
          <span style={{ background:'linear-gradient(135deg,#00c566,#00a854)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
            Predictions
          </span>
        </h1>

        <p style={{ fontSize:16, color:'#4a5a63', lineHeight:1.7, marginBottom:48, maxWidth:440 }}>
          Create prediction markets for your audience. Earn 0.5% royalties on every trade — with zero capital at risk.
        </p>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:48 }}>
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title} style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
              <div style={{ width:36, height:36, borderRadius:10, background:'rgba(0,197,102,.08)', border:'1px solid rgba(0,197,102,.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <Icon size={16} color="#00c566" strokeWidth={2}/>
              </div>
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:'#e8eaeb', marginBottom:2 }}>{title}</div>
                <div style={{ fontSize:11, color:'#4a5a63', lineHeight:1.5 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div style={{ display:'flex', gap:32 }}>
          {[['242+','Active Traders'],['$0','Creator Risk'],['0.5%','Your Royalty']].map(([v,l])=>(
            <div key={l}>
              <div style={{ fontSize:24, fontWeight:800, color:'#00c566', fontFamily:"'Syne',sans-serif" }}>{v}</div>
              <div style={{ fontSize:12, color:'#4a5a63' }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right Panel */}
      <div style={{ width:480, background:'#0d1114', borderLeft:'1px solid rgba(255,255,255,.06)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:48 }}>
        <div style={{ width:'100%', maxWidth:360 }}>
          <div style={{ background:'rgba(245,200,66,.08)', border:'1px solid rgba(245,200,66,.2)', borderRadius:12, padding:'10px 14px', marginBottom:24, display:'flex', alignItems:'center', gap:10 }}>
            <AlertCircle size={16} color="#f5c842" strokeWidth={2} style={{ flexShrink:0 }}/>
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:'#f5c842' }}>Beta - Under Active Development</div>
              <div style={{ fontSize:11, color:'#4a5a63' }}>Some features may be limited. Full launch coming soon!</div>
            </div>
          </div>

          <div style={{ textAlign:'center', marginBottom:32 }}>
            <img src="/logo.svg" alt="NaijaPredict" style={{ width:64, height:64, borderRadius:16, margin:'0 auto 16px', display:'block', objectFit:'cover' }}/>
            <h2 style={{ fontSize:24, fontWeight:800, marginBottom:8, fontFamily:"'Syne',sans-serif" }}>Creator Sign In</h2>
            <p style={{ fontSize:14, color:'#4a5a63', lineHeight:1.6 }}>Connect your Telegram account to access the creator dashboard</p>
          </div>

          {/* Telegram Login Widget */}
          <div style={{ display:'flex', justifyContent:'center', marginBottom:12 }} ref={tgRef}/>
          <div className="tg-login-status" style={{ textAlign:'center', fontSize:12, color:'#4a5a63', marginBottom:24, minHeight:16 }}></div>

          <div style={{ background:'rgba(0,197,102,.04)', border:'1px solid rgba(0,197,102,.12)', borderRadius:12, padding:'14px 16px', marginBottom:24 }}>
            <div style={{ fontSize:12, color:'#4a5a63', lineHeight:1.7 }}>
              By signing in, you agree to NaijaPredict's creator terms. Your Telegram identity verifies your creator account.
            </div>
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {[
              { icon: Shield, text: 'Verified via Telegram — no password needed' },
              { icon: Zap,    text: 'Instant access to creator dashboard' },
              { icon: DollarSign, text: 'Start earning from your first market' },
            ].map(({ icon: Icon, text }) => (
              <div key={text} style={{ display:'flex', alignItems:'center', gap:10, fontSize:12, color:'#4a5a63' }}>
                <Icon size={14} color="#00c566" strokeWidth={2}/>
                {text}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
